import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ordersApi } from '../../services/api/orders.api';
import { clearCart } from '../../store/cartSlice';
import {
  setPaymentMethod,
  setBankTransferReceipt,
  startProcessingOrder,
  completeOrder,
  resetCheckout,
  setOrderData
} from '../../store/features/checkoutSlice';
import { RadioGroup } from '@headlessui/react';
import { ChevronDownIcon, UploadIcon, CheckCircleIcon } from '@heroicons/react/outline';
import { settingsApi } from '../../services/api/settings.api';
import { toastService } from '../../services/toast';
import ReactDOM from 'react-dom';

const PaymentMethod = ({ id, title, icon, isSelected, onClick, children }) => (
  <div className="mt-3">
    <div 
      onClick={onClick}
      className={`flex w-full justify-between rounded-lg px-6 py-4 text-right text-base font-medium focus:outline-none transition-all duration-200 cursor-pointer border ${
        isSelected 
          ? 'bg-gray-50 dark:bg-gray-800/50 border-primary-500 dark:border-primary-400 ring-primary-500 dark:ring-primary-400' 
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
      }`}
    >
      <div className="flex items-center gap-4">
        <img src={icon} alt={title} className={`h-8 w-8 ${isSelected ? 'opacity-100' : 'opacity-70'}`} />
        <span className={isSelected ? 'text-primary-500 dark:text-primary-400' : 'text-gray-900 dark:text-white'}>
          {title}
        </span>
      </div>
      <ChevronDownIcon
        className={`${isSelected ? 'rotate-180 transform text-primary-500 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'} h-5 w-5 transition-all`}
      />
    </div>
    {isSelected && (
      <div className="mt-4 text-gray-900 dark:text-white">
        {children}
      </div>
    )}
  </div>
);

const Modal = ({ onClose, children }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm" />
      <div
        className="fixed z-50 inset-0"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md relative">
            {children}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const reduxSelectedMethod = useSelector((state) => state.checkout.selectedPaymentMethod);
  const receipt = useSelector((state) => state.checkout.bankTransfer.receipt);
  const [selectedMethod, setSelectedMethod] = useState(reduxSelectedMethod);
  const [receiptFile, setReceiptFile] = useState(null);
  const [installmentPlan, setInstallmentPlan] = useState(null);
  const [installmentPlanLoading, setInstallmentPlanLoading] = useState(false);
  const [showInstallmentConfirm, setShowInstallmentConfirm] = useState(false);
  const [orderStatus, setOrderStatus] = useState({
    processing: false,
    completed: false
  });
  const [settings, setSettings] = useState({
    bankAccounts: [],
    generalTerms: '',
    installmentTerms: ''
  });
  const [loading, setLoading] = useState(true);
  const [tapElement, setTapElement] = useState(null);
  const [orderSubmitted, setOrderSubmitted] = useState(false);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settings = await settingsApi.getPaymentSettings();
      setSettings({
        enableBankTransfer: settings.enableBankTransfer || false,
        enableOnlinePayment: settings.enableOnlinePayment || false,
        enableInstallment: settings.enableInstallment || false,
        enableLocalInstallment: settings.enableLocalInstallment || false,
        enablePaymentTerms: settings.enablePaymentTerms || false,
        bankAccounts: settings.bankAccounts || [],
        generalTerms: settings.generalTerms || '',
        installmentTerms: settings.installmentTerms || ''
      });
    } catch (error) {
      toastService.error('حدث خطأ أثناء تحميل الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // التحقق من تسجيل الدخول من sessionStorage وليس localStorage
    const token = sessionStorage.getItem('token');
    const user = sessionStorage.getItem('user');
    if (!token || !user) {
      // حفظ الـ URL الحالي في localStorage
      localStorage.setItem('redirectAfterLogin', '/checkout');
      // التوجيه لصفحة تسجيل الدخول
      navigate('/login');
      return;
    }

    window.scrollTo(0, 0);
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('[Checkout] \u{1F504} Fetching payment settings...');
        setLoading(true);
        const settings = await settingsApi.getPaymentSettings();
        setSettings({
          enableBankTransfer: settings.enableBankTransfer || false,
          enableOnlinePayment: settings.enableOnlinePayment || false,
          enableInstallment: settings.enableInstallment || false,
          enableLocalInstallment: settings.enableLocalInstallment || false,
          enablePaymentTerms: settings.enablePaymentTerms || false,
          bankAccounts: settings.bankAccounts || [],
          generalTerms: settings.generalTerms || '',
          installmentTerms: settings.installmentTerms || ''
        });
      } catch (error) {
        console.error('[Checkout] \u274C getPaymentSettings failed', error);
        console.error('Toast error triggered:', {
          error: error,
          message: error.message,
          code: error.code,
          isAxiosError: error.isAxiosError
        });
        
        // لا نعرض توست خطأ لما يكون مجرد request aborted
        if (error.code !== 'ECONNABORTED' && error.message !== 'Request aborted') {
          toastService.error('حدث خطأ أثناء تحميل طرق الدفع');
        }
      } finally {
        setLoading(false);
      }
    };

    if (cart.items.length === 0 && !orderSubmitted) {
      // إذا كانت السلة فارغة ولم يتم تقديم طلب، نوجه المستخدم إلى صفحة السلة
      navigate('/cart');
    } else {
      fetchData();
    }
  }, [cart.items.length, navigate, orderSubmitted]);

  // Removed Tap Element related code as we're using redirect method

  const handleMethodClick = async (methodId) => {
    if (selectedMethod === methodId) {
      setSelectedMethod(null);
      dispatch(setPaymentMethod(null));
      return;
    }
    setSelectedMethod(methodId);
    dispatch(setPaymentMethod(methodId));

    if (methodId === 'installment') {
      setInstallmentPlan(null);
      setInstallmentPlanLoading(true);
      try {
        const courseItem = cart.items.find(item => item.type === 'course');
        if (courseItem) {
          const plan = await ordersApi.getInstallmentPlan(courseItem.id, cart.totalAmount - (cart.discount || 0));
          setInstallmentPlan(plan);
        }
      } catch (err) {
        setInstallmentPlan(null);
      } finally {
        setInstallmentPlanLoading(false);
      }
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // التحقق من نوع الملف (صورة أو PDF)
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!validImageTypes.includes(file.type)) {
      toastService.error('يرجى اختيار صورة أو ملف PDF صالح');
      return;
    }

    // التحقق من حجم الملف (أقل من 5 ميجابايت)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toastService.error('حجم الملف كبير جدًا. يجب أن يكون أقل من 5 ميجابايت');
      return;
    }

    setReceiptFile(file);
    dispatch(setBankTransferReceipt(file));

    // عرض معاينة للصورة
    const reader = new FileReader();
    reader.onload = (e) => {
      const previewElement = document.getElementById('receipt-preview');
      if (previewElement) {
        if (file.type === 'application/pdf') {
          previewElement.innerHTML = `<div class="flex items-center justify-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-red-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
            </svg>
            <span class="text-sm font-medium">${file.name}</span>
          </div>`;
        } else {
          previewElement.innerHTML = `<img src="${e.target.result}" alt="معاينة الإيصال" class="max-h-40 rounded-lg shadow-sm" />`;
        }
      }
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmPayment = async () => {
    if (selectedMethod === 'installment') {
      setShowInstallmentConfirm(true);
      return;
    }
    await submitOrder();
  };

  const submitOrder = async () => {
    try {
      setLoading(true);
      // تعيين حالة الطلب كقيد التنفيذ
      setOrderSubmitted(true);

      // تحقق من وجود إيصال إذا كانت طريقة الدفع هي التحويل البنكي
      if (selectedMethod === 'bank-transfer' && !receiptFile) {
        toastService.error('يرجى إرفاق صورة الإيصال');
        setLoading(false);
        setOrderSubmitted(false);
        return;
      }

      // إذا كانت طريقة الدفع هي التحويل البنكي، قم برفع الإيصال أولاً
      let receiptId = null;
      if (selectedMethod === 'bank-transfer' && receiptFile) {
        const formData = new FormData();
        formData.append('receipt', receiptFile);
        
        try {
          const uploadResponse = await ordersApi.uploadReceipt(formData);
          receiptId = uploadResponse.receiptId;
        } catch (error) {
          // خطأ في رفع الإيصال
          toastService.error('حدث خطأ أثناء رفع الإيصال');
          setLoading(false);
          setOrderSubmitted(false);
          return;
        }
      }

      const orderData = {
        items: cart.items.map(item => ({
          id: item.id,
          type: item.type || 'book',
          quantity: item.quantity || 1,
          price: item.price
        })),
        paymentMethod: selectedMethod,
        totalAmount: cart.totalAmount,
        discount: cart.discount || 0,
        finalTotal: cart.totalAmount - (cart.discount || 0)
      };

      // إضافة كود الكوبون إذا كان موجود
      if (cart.couponCode) {
        orderData.couponCode = cart.couponCode;
        orderData.couponDetails = cart.couponDetails;
      }

      // إضافة معرف الإيصال إذا كانت طريقة الدفع هي التحويل البنكي
      if (selectedMethod === 'bank-transfer' && receiptId) {
        orderData.receiptId = receiptId;
      }

      // لوج البيانات اللي هتتمرر للـ API
      console.log('🚨 Checkout.js - Order Data to API:', {
        orderData,
        cart_items: cart.items,
        selected_method: selectedMethod,
        receipt_id: receiptId,
        cart_total: cart.totalAmount,
        cart_discount: cart.discount,
        cart_coupon: cart.couponCode
      });

      // إرسال الطلب
      const response = await ordersApi.submitOrder(orderData);

      // حفظ بيانات الطلب في الـ Redux
      dispatch(setOrderData(response));

      // تنظيف السلة وإعادة ضبط حالة الـ checkout
      dispatch(clearCart());
      dispatch(resetCheckout());

      if (response.payment_url) {
        console.log('Received payment_url:', response.payment_url);
        // إضافة اللغة العربية لصفحة الدفع في Tap
        try {
          const payUrl = new URL(response.payment_url);
          if (payUrl.searchParams.has('language')) {
            payUrl.searchParams.set('language', 'ar');
          } else {
            payUrl.searchParams.set('lang', 'ar');
          }
          window.location.href = payUrl.toString();
        } catch (e) {
          // fallback لو حصل خطأ فى URL()
          const sep = response.payment_url.includes('?') ? '&' : '?';
          window.location.href = `${response.payment_url}${sep}language=ar`;
        }
      } else if (response.orderId) {
        // في حالة التحويل البنكي
        toastService.success('تم تأكيد طلبك بنجاح');
        
        // استخدام window.location.href بدلاً من navigate
        window.location.href = `/order-confirmation/${response.orderId}`;
      } else {
        toastService.error('حدث خطأ أثناء إنشاء الطلب');
        setOrderSubmitted(false);
      }
    } catch (error) {
      // خطأ في معالجة الطلب
      toastService.error(error.response?.data?.message || 'حدث خطأ أثناء معالجة الطلب');
      setOrderSubmitted(false);
    } finally {
      setLoading(false);
    }
  };

  const allPaymentMethods = [
    {
      id: 'card',
      title: 'دفع إلكتروني',
      icon: '/icons/credit-card.svg',
      requiresOnlinePayment: true,
      content: (
        <div className="space-y-4">
          <div className="relative p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                سيتم تحويلك إلى صفحة الدفع الآمنة لإتمام عملية الشراء
              </p>
              <div className="flex justify-center space-x-4 rtl:space-x-reverse">
                <img src="/icons/visa.svg" alt="Visa" className="h-8" />
                <img src="/icons/mastercard.svg" alt="Mastercard" className="h-8" />
                <img src="/icons/mada.svg" alt="mada" className="h-8" />
              </div>
            </div>
          </div>
        </div>
      )
    },
    // خيار تمارا مخفي ولكن موجود في الكود
    {
      id: 'tamara',
      title: 'قسم فاتورتك على 4 دفعات',
      icon: '/icons/tamara.svg',
      requiresInstallment: true,
      content: (
        <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
          <p className="text-lg font-medium mb-6 text-center text-gray-900 dark:text-white">قسم المبلغ على 4 دفعات شهرية بدون فوائد</p>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="font-bold text-xl text-primary-600 dark:text-white">
                {(cart.totalAmount / 4).toFixed(2)} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">الآن</div>
            </div>
            <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="font-bold text-xl text-primary-600 dark:text-white">
                {(cart.totalAmount / 4).toFixed(2)} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">بعد شهر</div>
            </div>
            <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="font-bold text-xl text-primary-600 dark:text-white">
                {(cart.totalAmount / 4).toFixed(2)} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">بعد شهرين</div>
            </div>
            <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="font-bold text-xl text-primary-600 dark:text-white">
                {(cart.totalAmount / 4).toFixed(2)} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">بعد 3 أشهر</div>
            </div>
          </div>
        </div>
      )
    },
    // تقسيط محلي (عبر المنصة)
    {
      id: 'installment',
      title: 'تقسيط (مع دوز)',
      icon: '/icons/credit-card.svg',
      requiresLocalInstallment: true,
      content: (
        <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
          {installmentPlanLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : installmentPlan?.available ? (
            <>
              <p className="text-lg font-medium mb-6 text-center text-gray-900 dark:text-white">
                قسم المبلغ على {installmentPlan.installment_count} دفعات
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                {installmentPlan.schedule.map((item, idx) => (
                  <div key={idx} className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="font-bold text-xl text-primary-600 dark:text-white">
                      {item.amount.toFixed(2)} <span style={{ fontFamily: 'saudi_riyal', fontSize: '1.1em' }}>&#xE900;</span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {idx === 0 ? 'الآن' : `بعد ${idx} شهر${idx > 1 ? '' : ''}`}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400">
              {installmentPlan ? 'المدة المتبقية من الدورة لا تسمح بنظام التقسيط' : 'سيتم تقسيم المبلغ على دفعات شهرية حسب مدة الدورة'}
            </p>
          )}
        </div>
      )
    },
    {
      id: 'bank-transfer',
      title: 'تحويل بنكي',
      icon: '/icons/bank.svg',
      requiresBankTransfer: true,
      content: (
        <div className="space-y-4">
          {settings.bankAccounts?.map((account, index) => (
            <div key={index} className="relative bg-gradient-to-br from-slate-600 to-slate-800 dark:from-gray-800 dark:to-gray-900 rounded-xl p-6 overflow-hidden shadow-lg w-full md:max-w-md mx-auto">
              {/* Pattern Background */}
              <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
              
              {/* Feather Pattern */}
              <div className="absolute top-0 right-0 w-48 h-48 opacity-10">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-full h-full fill-current">
                  <path d="M92.5,7.5C84.4,15.6,76.4,23.6,68.3,31.7c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6 c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6 c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9 c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9 c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9 c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9 c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6 c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9C7.5,92.5,7.5,7.5,92.5,7.5z"/>
                </svg>
              </div>

              {/* Shine Effect */}
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-white opacity-10 rotate-12 transform scale-y-100 rounded-full blur-xl"></div>
              
              <div className="relative space-y-4 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm opacity-80">اسم صاحب الحساب</p>
                    <p className="text-base font-semibold mt-1">{account.account_holder}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>

                <div>
                  <p className="text-sm opacity-80">رقم IBAN</p>
                  <p className="text-base font-mono font-semibold mt-1" dir="ltr">{account.iban}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm opacity-80">رقم الحساب</p>
                    <p className="text-base font-mono font-semibold mt-1" dir="ltr">{account.account_number}</p>
                  </div>

                  <div>
                    <p className="text-sm opacity-80">اسم البنك</p>
                    <p className="text-base font-semibold mt-1">{account.bank_name}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="mt-6">
            <input
              type="file"
              id="receipt"
              className="hidden"
              accept="image/*, application/pdf"
              onChange={handleFileChange}
            />
            <label htmlFor="receipt" className="block cursor-pointer">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-center gap-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <UploadIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm text-gray-900 dark:text-white">
                    {receiptFile ? receiptFile.name : 'إيصال التحويل البنكي'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {receiptFile ? 'تم رفع الإيصال، اضغط للتغيير' : 'اضغط لاختيار ملف'}
                  </span>
                </div>
              </div>
            </label>
          </div>
        </div>
      )
    }
  ];

  // فلترة طرق الدفع حسب الإعدادات
  const availablePaymentMethods = allPaymentMethods.filter(method => {
    // التحقق من توفر طريقة الدفع وفقًا للإعدادات
    if (method.hidden) return false; // إخفاء الخيارات المعلمة كـ hidden
    if (method.requiresOnlinePayment && !settings.enableOnlinePayment) return false;
    if (method.requiresInstallment && !settings.enableInstallment) return false;
    if (method.requiresLocalInstallment && !settings.enableLocalInstallment) return false;
    if (method.requiresBankTransfer && (!settings.enableBankTransfer || !settings.bankAccounts?.length)) return false;
    return true;
  });

  // طرق الدفع المتاحة بعد الفلترة

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (cart.items.length === 0 && !orderSubmitted) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen pt-16">
      <div className="max-w-6xl mx-auto px-2 sm:px-6 py-2">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Right Side - Payment Methods */}
          <div className="lg:col-span-8">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-primary-500/5 border border-gray-200/80 dark:border-gray-700/50 mb-5">
              <div className="px-6 py-5">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">طرق الدفع</h2>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="space-y-3">
              {availablePaymentMethods.map((method) => (
                <PaymentMethod
                  key={method.id}
                  id={method.id}
                  title={method.title}
                  icon={method.icon}
                  isSelected={selectedMethod === method.id}
                  onClick={() => handleMethodClick(method.id)}
                >
                  {selectedMethod === method.id && method.content}
                </PaymentMethod>
              ))}
            </div>

            {/* عرض محتوى طريقة الدفع المختارة */}
          </div>

          {/* Left Side - Order Summary */}
          <div className="lg:col-span-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-primary-500/5 border border-gray-200/80 dark:border-gray-700/50 p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">ملخص الطلب</h3>
              <div className="space-y-4">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>الإجمالي</span>
                  <span>{cart.totalAmount} ر.س</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>الخصم</span>
                  <span>{cart.discount || 0} ر.س</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white pt-4 border-t border-gray-200 dark:border-gray-700">
                  <span>المجموع</span>
                  <span>{cart.totalAmount - (cart.discount || 0)} ر.س</span>
                </div>
              </div>

              <button
                onClick={handleConfirmPayment}
                disabled={!selectedMethod || (selectedMethod === 'bank-transfer' && !receiptFile)}
                className="w-full mt-6 px-6 py-3 text-base font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                تأكيد
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* تاكيد شروط التقسيط */}
      {showInstallmentConfirm && (
        <Modal onClose={() => setShowInstallmentConfirm(false)}>
          <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <div className="flex items-center justify-center mb-3">
                <CheckCircleIcon className="h-10 w-10 text-primary-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">شروط التقسيط</h3>
              <div className="text-right mb-4">
                {settings.installmentTerms ? (
                  <div className="whitespace-pre-wrap text-gray-600 dark:text-gray-300">
                    {settings.installmentTerms}
                  </div>
                ) : (
                  <p className="text-gray-600 dark:text-gray-300">لا توجد شروط للعرض</p>
                )}
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    setShowInstallmentConfirm(false);
                    submitOrder();
                  }}
                  className="px-5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  موافق
                </button>
                <button
                  onClick={() => setShowInstallmentConfirm(false)}
                  className="px-5 py-2 text-sm font-medium text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Checkout;
